/// <amd-module name="Build/Designer/EditorView" />
import * as React from "react";

import { Build, BuildDefinition, BuildDefinitionVariable, BuildRepository, BuildRequestValidationResult,
         DefinitionQueueStatus, DefinitionResourceReference, YamlProcess } from "Build/Client/Contracts/Build";
import { CheckForHostedMacDataRequestParams, CheckForHostedMacData, PipelineServiceConnection } from "Build/Client/Contracts/DataProvider";
import { IBuildLinkingService } from "Build/Client/Linking";

import { IQueueBuildProps } from "Build/Common/Commands/QueueBuild/Command.types";
import { CommandComponent, prefetch } from "Build/Common/Library/Components/CommandComponent";
import { BuildOptionGUIDs } from "Build/Common/Library/Constants";
import { PipelineSettings, PipelineSettingsPanel } from "Build/Common/Library/Legacy/Components/PipelineSettingsPanel";
import { openLinkSecurely } from "Build/Common/Library/Navigation";
import { IPipelineResourcesService } from "Build/Common/Library/PipelineResourcesService";
import * as LibraryResources from "Build/Common/Library/Resources";
import { clearTreeStateOnUnmount } from "Build/Common/Library/StateHandler";
import { RepositoryDescriptor } from "Build/Common/Library/Types";

import { EditorConstants, LegacyDesignerTabKeys, WellKnownExceptions } from "Build/Designer/Constants";
import { BuildMessageInfo, SaveResult } from "Build/Common/Library/Designer.types";
import { EditorAuthLandingPage, getSourceProviderTile } from "Build/Designer/EditorAuthLandingPage";
import { EditorHeader } from "Build/Designer/EditorHeader";
import { IInstallAppService } from "Build/Common/Library/InstallAppService";
import { addNavigateAwayListener, getLoseChangesDialog, removeNavigateAwayListener, showLoseChangesDialog } from "Build/Designer/LoseChangesDialog";
import { PageLoadScenarios, PerformanceConstants } from "Build/Designer/Performance";
import { IPipelineEditorBreadcrumbService } from "Build/Designer/PipelineEditorBreadcrumbService";
import { IPipelineService } from "Build/Common/Library/PipelineService";
import { PipelineVariablesPanel } from "Build/Designer/PipelineVariablesPanel";
import * as Resources from "Build/Designer/Resources";
import { SavePanel } from "Build/Designer/SavePanel";
import { showWarning, clearWarning, BannerContent } from "Build/Designer/WarningBanner";
import { CommitDescriptor,FileContentInfo, IYamlFileService } from "Build/Designer/YamlFileService";
import { IYamlResourcesService, NamedBuildDefinitionVariable } from "Build/Designer/YamlResourcesService";
import { YamlTab } from "Build/Designer/YamlTab";
import { IPipelinesRestClient } from "Pipelines/Client/RestClient/Pipelines";
import { RepositoryResourceParameters, Run, RunPipelineParameters, RunResourcesParameters, PipelineResourceParameters } from "Pipelines/Client/Contracts/Pipelines";
import { ServiceConnectionRecommendation} from "Build/Common/Library/Components/UpgradeServiceConnectionHeader"

import { QueuePanel } from "Build/Queue-Panel/QueuePanel";

import { ISourceProvider, RepositoryTypes } from "Build/SourceProviders/Common";
import { ISourceProviderService } from "Build/SourceProviders/SourceProvider";

import { IGlobalMessagesService } from "TFS/GlobalBanner/GlobalMessagesService";
import { ITfsNavigationService } from "Tfs/Platform/Navigation";

import { equals, format } from "VSS/Core/Util/String";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { FormatComponent } from "VSS/Platform/Components/Format";
import { IServerException, IVssContributionService } from "VSS/Platform/Contribution";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { VssServerError } from "VSS/Platform/Fetch";
import { FastPageSwitch } from "VSS/Platform/FPS";
import { IVssHistoryService } from "VSS/Platform/History";
import { Components, IVssComponentContext, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";
import { IVssPerformanceService } from "VSS/Platform/Performance";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

import { isSafeProtocol } from "VSS/Features/Markdown/Utils/UrlHelper";

import { IButtonProps } from "VSSUI/Button";
import { Dialog, IDialogProps } from "VSSUI/Dialog";
import { MessageCard, MessageCardSeverity } from "VSSUI/MessageCard";
import { Page } from "VSSUI/Page";

interface PipelineEditorAuthenticationRequirements {
    isLoginRequired: boolean;
    shouldShowLandingPage: boolean;
    loginUrl?: string;
}

interface PipelineEditorData {
    definition: BuildDefinition;
    content: string;
    yamlFileUrl: string;
    branch: string;
    objectId: string;
    fileErrorMessage: string;
    serviceConnection: PipelineServiceConnection;
    authenticationRequirements: PipelineEditorAuthenticationRequirements;
}

interface Props {
}

interface State {
    definition?: BuildDefinition;
    login?: string;
    connection: PipelineServiceConnection;
    yamlContent: string;
    yamlFileUrl: string;
    yamlBranch: string;
    yamlFileObjectId: string;
    isYamlDirty: boolean;
    isVariablesDirty?: boolean;
    errorMessage: string;
    errorActions?: IButtonProps[];
    isErrorMessageDismissable: boolean;

    saveErrorMessage?: string;
    savePanelIsOpen: boolean;
    runPanelIsOpen: boolean;
    isYamlSaving: boolean;

    settingsPanelIsOpen: boolean;
    isPipelineSaving: boolean;
    settingsErrorMessage?: string;

    variablesPanelIsOpen: boolean;
    variablesErrorMessage?: string;

    buildMessageInfo?: BuildMessageInfo;
    isBuildRunning: boolean;

    isEditorReady: boolean;

    useAppConnectionDescription?: string;
    useAppConnectionAction?: IButtonProps;

    authentication: PipelineEditorAuthenticationRequirements;

    validationDialogOpen?: boolean;
    validationDialogMessage?: string;
}

class EditorView extends VssComponent<Props, State, IVssComponentContext> {
    private _ttiRecorded: boolean = false;
    private _editor: any; /*IEditor*/
    private _lastCleanContent: string = "";
    private _yamlResourceService: IYamlResourcesService;
    private _authorizeAfterSave = false;
    private _sourceProviderName: string;
    private _onQueueDialogDismiss?: () => void;

    constructor(props: Props, context: IVssComponentContext) {
        super(props, context);

        const state: State = {
            isEditorReady: false,
            connection: {} as PipelineServiceConnection,
            savePanelIsOpen: false,
            runPanelIsOpen: false,
            isYamlSaving: false,
            yamlContent: "",
            yamlFileUrl: "",
            yamlBranch: "",
            yamlFileObjectId: "",
            isYamlDirty: false,
            errorMessage: "",
            isErrorMessageDismissable: false,
            settingsPanelIsOpen: false,
            variablesPanelIsOpen: false,
            isPipelineSaving: false,
            isBuildRunning: false,
            useAppConnectionAction: undefined,
            authentication: {
                isLoginRequired: false,
                shouldShowLandingPage: false,
            },
        };

        // by doing getData instead of getDataAsync, we don't render twice on page load!
        const PipelineDataProviderKey = "ms.vss-build-web.pipeline-editor-data-provider";
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const editorData = contributionService.getData<PipelineEditorData>(PipelineDataProviderKey);
        if (editorData) {
            this._lastCleanContent = editorData.content || "";

            state.definition = editorData.definition;
            state.yamlBranch = editorData.branch || (editorData.definition.repository.defaultBranch || "").replace("refs/heads/", "");
            state.yamlContent = editorData.content;
            state.yamlFileUrl = editorData.yamlFileUrl;
            state.yamlFileObjectId = editorData.objectId;
            state.errorMessage = editorData.fileErrorMessage;

            if (state.errorMessage) {
                const buildRepo = state.definition.repository;
                if (equals(buildRepo.type, RepositoryTypes.TfsGit, true) && buildRepo.id && buildRepo.name) {
                    state.errorMessage = state.errorMessage.replace(buildRepo.id, buildRepo.name);
                }
            }

            state.authentication = editorData.authenticationRequirements;
            state.connection = editorData.serviceConnection || {};

            // External source providers like GitHub require user authentication to view/edit the YAML.
            if (state.authentication.isLoginRequired) {
                state.yamlBranch = "";
                if (state.authentication.shouldShowLandingPage) {
                    state.errorMessage = "";
                }
                else {
                    const sourceProviderTile = getSourceProviderTile(this.context.pageContext, state.definition.repository.type.toLowerCase());
                    this._sourceProviderName = sourceProviderTile ? sourceProviderTile.name : "";
                }
                if (!state.authentication.loginUrl) {
                    state.errorMessage = Resources.LoginUrlNotSpecified;
                }
            }
        }
        else {
            const ex: IServerException = contributionService.getDataProviderExceptions()[PipelineDataProviderKey];
            this._handleEditorDataProviderError(ex, state);
        }

        this._yamlResourceService = context.pageContext.getService<IYamlResourcesService>("IYamlResourcesService");
        this.state = state;

        // Update the URL to include the branch in case it wasn't specified in the request.
        if (state.yamlBranch && state.yamlBranch !== this.historyService.getState().branch) {
            this._updateUrl();
        }

    }

    public componentDidMount(): void {
        super.componentDidMount();
        prefetch(this.context.pageContext);
        this.navigationService.setPageTitle(this._getTitle());
        this.breadcrumbService.setPipeline(this.state.definition);

        // If login is required, redirect after the initial render so we get an Authorizing spinner
        if (this.state.authentication.isLoginRequired
            && !this.state.authentication.shouldShowLandingPage
            && this.state.authentication.loginUrl) {
            openLinkSecurely(this.state.authentication.loginUrl);
        }
        else {
            this._yamlResourceService.subscribe(this._onChangeVariables);
            addNavigateAwayListener(this._onNavigateAway);

            this._checkForAppConnectionUpgrade();
        }

        this._checkForHostedMac();
    }

    public componentWillUnmount(): void {
        super.componentWillUnmount();
        this._yamlResourceService.unsubscribe(this._onChangeVariables);
        this.breadcrumbService.clearPipeline();
        removeNavigateAwayListener(this._onNavigateAway);
        clearTreeStateOnUnmount(this.context.pageContext);
    }

    public render(): JSX.Element {
        // If the definition is not found, return a minimal header with the error message
        if (!this.state.definition) {
            return this._getEmptyPage();
        }

        // If the yaml is protected, require login before showing it (show landing page or redirect after render)
        if (this._shouldShowLandingPage()) {
            return this._getAuthLandingPage();
        }

        // Otherwise, show the editor page as expected
        return this._getEditorPage();
    }

    private _shouldShowLandingPage(): boolean {
        return this.state.authentication.isLoginRequired && this.state.authentication.shouldShowLandingPage;
    }

    private _getEmptyPage(): JSX.Element {
        return (
            <Page className={pageClassName}>
                {this._getHeader()}
            </Page>
        );
    }

    private _getAuthLandingPage(): JSX.Element{
        const definition = this.state.definition!;
        return (
            <Page className={pageClassName}>
                {this._getHeader()}
                <EditorAuthLandingPage
                    loginUrl={this.state.authentication.loginUrl}
                    yamlFileLink={this.state.yamlFileUrl}
                    repository={definition.repository}
                    sourceProvider={this._getSourceProvider()} />
            </Page>
        );
    }

    private _getEditorPage() {
        const definition = this.state.definition!;
        const yamlProcess = definition.process as YamlProcess;
        const yamlFilename = yamlProcess.yamlFilename;
        const shortFileName = this._getFileName(yamlFilename);
        const commitMessage = shortFileName ? format(Resources.SavePanelCommitMessageForEdit, shortFileName) : "";
        const sourceProvider = this._getSourceProvider();
        const canCreatePr = !equals(definition.repository.type, RepositoryTypes.Bitbucket, true)
        || isFeatureFlagEnabled(this.context.pageContext, "Build2.BitbucketAzurePipelinesOAuthClient", false);

        let spinnerMessage = "";
        if (this.state.authentication.isLoginRequired) {
            spinnerMessage = this._sourceProviderName ?
                format(Resources.AuthorizingWithProviderMessage, this._sourceProviderName) : Resources.AuthorizingMessage;
        }

        const dialogProps: IDialogProps = {
            onDismiss: () => { this.setState({ validationDialogOpen: false }); },
            lightDismiss: true,
            modal: true,
            contentSize: 3,
            titleProps: { text: Resources.Validation },
            footerButtonProps: [
                {
                    text: Resources.OK,
                    onClick: () => { this.setState({ validationDialogOpen: false }); },
                    primary: true
                }
            ]
        };

        // Check for cross-site scripting
        let repositoryUrl = definition.repository && definition.repository.properties && definition.repository.properties['manageUrl'];
        if (repositoryUrl && !repositoryUrl.toLowerCase().startsWith('http')) {
            repositoryUrl = ""
        }

        return (
            <Page className={pageClassName}>
                {this._getHeader()}
                <YamlTab
                    content={this.state.yamlContent}
                    editor={this._editor}
                    spinnerMessage={spinnerMessage}
                    onContentUpdated={this._onYamlContentUpdated}
                    onEditorCreated={this._onEditorCreated}
                    onEditorDisposing={this._onEditorDisposing}
                    sidePanelInitiallyOpen={true}
                    repositoryName={definition.repository.name}
                    repositoryIconName={sourceProvider.getRepoIconName()}
                    repositoryUrl={repositoryUrl}
                    branchName={this.state.yamlBranch}
                    fileName={yamlFilename}
                    fileUrl={this.state.yamlFileUrl}
                    supportFPS={equals(definition.repository.type, RepositoryTypes.TfsGit, true)}
                    isYamlDirty={this.state.isYamlDirty}
                    definition={definition}
                    onBranchSelected={this._onBranchSelected} />
                <SavePanel
                    errorMessage={this.state.saveErrorMessage}
                    fileName={yamlFilename}
                    commitMessage={commitMessage}
                    defaultBranch={this.state.yamlBranch}
                    isOpen={this.state.savePanelIsOpen}
                    isSaving={this.state.isYamlSaving}
                    saveAndRun={false}
                    canCreatePr={canCreatePr}
                    onSave={this._onYamlSave}
                    onDismiss={this._dismissSavePanel}
                    showCancelButton={true} />

                { this.state.runPanelIsOpen &&
                    <QueuePanel
                        showPanel={this.state.runPanelIsOpen}
                        pageContext={this.context.pageContext}
                        projectId={definition.project.id}
                        definitionId={definition.id}
                        buildRepository={definition.repository}
                        sourceBranch={this.state.yamlBranch || ""}
                        runtimeVariables={this._getOverridableVariables(definition.variables)}
                        onDismiss={this._onRunPanelClosed}/>}

                <PipelineSettingsPanel
                    key={definition.revision}
                    definition={definition}
                    branch={this.state.yamlBranch}
                    isYamlDirty={this.state.isYamlDirty}
                    errorMessage={this.state.settingsErrorMessage}
                    isOpen={this.state.settingsPanelIsOpen}
                    isSaving={this.state.isPipelineSaving}
                    onSave={this._onSavePipelineSettings}
                    onDismiss={this._dismissPipelineSettingsPanel}
                    onError={(message) => this.setState({ settingsErrorMessage: message })} />

                {this.state.variablesPanelIsOpen &&
                    <PipelineVariablesPanel
                        variables={definition.variables}
                        errorMessage={this.state.variablesErrorMessage}
                        isSaving={this.state.isPipelineSaving}
                        onSave={this._onSavePipelineVariables}
                        onDismiss={this._dismissPipelineVariablesPanel} />
                }

                {this.state.validationDialogOpen &&
                    <Dialog {...dialogProps}>
                        {this.state.validationDialogMessage}
                    </Dialog>
                }
            </Page>
        );
    }

    // The QueuePanel expects an array of variables that can be overridden.  Filter, copy, and convert from a dictionary.
    private _getOverridableVariables(variables: { [key: string]: BuildDefinitionVariable })
        : { name: string, variable: BuildDefinitionVariable }[] {
        return Object.keys(variables || {})
            .filter(key => variables[key].allowOverride)
            .map(key => { return { name: key, variable: { ...variables[key] } } });
    }

    private _onNavigateAway = (ev: BeforeUnloadEvent | CustomEvent) => {
        if (this.state.isYamlDirty || this.state.isPipelineSaving) {
            showLoseChangesDialog(this.context.pageContext, ev);
        }
    }

    private _getFileName(fullFileName: string) {
        let fileName = fullFileName || "";
        const startIndex = fileName.lastIndexOf("/") + 1;
        if (startIndex > 0 && fileName.length > startIndex) {
            fileName = fileName.substr(startIndex);
        }
        return fileName;
    }

    private _getHeader(): JSX.Element {
        return <>
            <EditorHeader
                definition={this.state.definition}
                isYamlDirty={this.state.isYamlDirty}
                actionButtonLabel={this.state.isYamlDirty ? Resources.SaveButtonLabel : Resources.RunButtonLabel}
                actionDisabled={!!this.state.errorMessage || this.state.isBuildRunning || this.state.isPipelineSaving}
                onActionClick={this.state.isYamlDirty ? this._showSavePanel : this._showRunPanel}
                onFullYaml={this._fullYaml}
                onRunWithParameters={this._runBuildWithParameters}
                onSettingsOpen={this._showPipelineSettingsPanel}
                onValidateYaml={this._validateYaml}
                onVariablesOpen={this._showPipelineVariablesPanel}
                buildMessageInfo={this.state.buildMessageInfo}
                onBuildMessageDismissed={this._clearBuildMessage}
                errorMessage={this.state.errorMessage}
                errorActions={this.state.errorActions}
                onErrorMessageDismissed={this.state.isErrorMessageDismissable ? this._clearErrorMessage : undefined}
                title={this._getTitle()} />
            {this._getAppUpgradeHeader()}
        </>;
    }

    private _getAppUpgradeHeader(): JSX.Element {
        if (!isFeatureFlagEnabled(this.context.pageContext, "Build2.SwitchToAppConnection", false) || !this.state.useAppConnectionAction) {
            return <></>;
        }

        return <div className="page-content flex-row flex-noshrink">
            <MessageCard
                className="flex-grow"
                severity={MessageCardSeverity.Info}
                onDismiss={this._closeConnectionCard}
                buttonProps={[this.state.useAppConnectionAction]}>
                <FormatComponent format={this.state.useAppConnectionDescription!}>
                    <FPSLink
                        href={this.state.connection.appHelpLink}>
                        {this.state.connection.appName}
                    </FPSLink>
                </FormatComponent>
            </MessageCard>
        </div>;
    }

    private async _checkForAppConnectionUpgrade() {
        if (isFeatureFlagEnabled(this.context.pageContext, "Build2.SwitchToAppConnection", false) &&
                !this._shouldShowLandingPage() && !this.state.connection.isAppConnection && !!this.state.connection.appName) {
            try {
                const definition = this.state.definition!;
                const nonce = this.state.connection.nonce;
                const serviceConnectionRecommendation = await this._getRecommendedServiceConnection(definition, nonce);
                const recommendedConnectionId = serviceConnectionRecommendation.commonConnectionId;
                if (recommendedConnectionId) {
                    // there is an app connection available
                    if (this.state.connection.autoUpgradeToAppConnection) {
                        // automatically switch to start using it, the user has likely gone through 'installApp'
                        this._switchToAppConnection(recommendedConnectionId);
                    }
                    else {
                        // there is an existing app connection, so offer to change to that connection
                        const buttonProps: IButtonProps = {
                            text: LibraryResources.ChangeToAppConnection,
                            onClick: () => this._switchToAppConnection(recommendedConnectionId)
                        };
                        this.setState({ useAppConnectionAction: buttonProps, useAppConnectionDescription: LibraryResources.PreferAppConnectionChange });
                    }
                }
                else if (!serviceConnectionRecommendation.appInstalledForRepository && serviceConnectionRecommendation.userCanInstall) {
                    // the App is not installed, but the user is able to install it, so offer to install it
                    const buttonProps: IButtonProps = {
                        text: LibraryResources.InstallApp,
                        onClick: () =>
                            this._installApp(
                                definition.id,
                                nonce,
                                serviceConnectionRecommendation.installAppUrlRoot,
                                definition.repository)
                    };
                    this.setState({ useAppConnectionAction: buttonProps, useAppConnectionDescription: LibraryResources.PreferAppConnectionInstall });
                }
            }
            catch(error) {
                const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
                telemetryService.publishEvent("Build", "Editor.CheckForAppConnectionUpgradeError",  {
                    message: error && error.message,
                    error
                });
            }
        }
    }

    private _getRecommendedServiceConnection(definition: BuildDefinition, nonce: string): Promise<ServiceConnectionRecommendation> {
        const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
        const dataProviderId = "ms.vss-build-web.app-serviceconnections-recommendation-data-provider";
        const parameters = {
            sourceProvider: this._getSourceProvider().getKey(),
            repositoryId: definition.repository.id,
            repositoryName: definition.repository.name,
            connectionId: this.state.connection.connectionId,
            strongBoxKey: "useWellKnownStrongBoxLocation",
            createOAuthConnectionIfNeeded: false,
            login: this.state.connection.login,
        };
        return contributionService.getDataAsync<ServiceConnectionRecommendation>(dataProviderId, parameters, true)
            .then((serviceConnectionRecommendation) => {
                if (!serviceConnectionRecommendation) {
                    const exception = contributionService.getDataProviderExceptions()[dataProviderId];
                    throw new Error(exception && exception.message);
                }
                return serviceConnectionRecommendation;
            });
    }

    private _clearBuildMessage = () => {
        if (this.state.buildMessageInfo) {
            this.setState({ buildMessageInfo: undefined }, this._resizeEditor);
        }
    }

    private _clearErrorMessage = () => {
        if (this.state.errorMessage) {
            this.setState({ errorMessage: "", errorActions: undefined }, this._resizeEditor);
        }
    }

    private _closeConnectionCard = () => {
        this.setState({ useAppConnectionAction: undefined }, this._resizeEditor);
    }

    private _switchToAppConnection = async (connectionId: string) => {
        const definition = this.state.definition!;
        definition.repository.properties = definition.repository.properties || [];
        definition.repository.properties["connectedServiceId"] = connectionId;

        this._closeConnectionCard();
        this.setState({ isPipelineSaving: true, settingsErrorMessage: "" });

        try {
            await this.pipelineService.savePipeline(definition);

            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            await contributionService.getDataAsync<boolean>("ms.vss-build-web.pipeline-edit-continuation-data-provider", {
                complete: true
            }, true);

            this.setState({ definition, isPipelineSaving: false }, this._closeConnectionCard);

            const telemetryService = this.context.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
            telemetryService.publishEvent("Build", "Editor.AppConnectionUpgradeFromOAuth",  {
                GitHubApp: "AddAppConnection",
                GitHubAppRepo: definition.repository.id,
                action: "ChangeConnectionToGitHubApp",
            });

            // show a toast on complete if this was a user-driven action
            if (!this.state.connection.autoUpgradeToAppConnection) {
                this.globalMessageService.addToast({
                    message: LibraryResources.ConnectionChanged,
                    duration: EditorConstants.ToastDuration
                });
            }
        }
        catch (e) {
            const message = (e && e.message) || LibraryResources.SavePipelineErrorMessage;
            this._handleSettingsError(message);
        }
    }

    private _installApp = async (pipelineId: number, nonce: string, installAppUrlRoot: string, repository: BuildRepository) => {
        const installAppService = this.context.pageContext.getService<IInstallAppService>("IInstallAppService");
        installAppService.installAppForEdit(pipelineId, nonce, installAppUrlRoot, repository);
    }

    private _getSourceProvider(): ISourceProvider {
        const sourceProviderService = this.context.pageContext.getService<ISourceProviderService>("ISourceProviderService");
        return this.state.definition && sourceProviderService.getSourceProvider(this.state.definition.repository.type);
    }

    private _getTitle(): string {
        return (this.state.definition && this.state.definition.name) || Resources.EditorDefaultTitle;
    }

    private _updateUrl() {
        const historyService = this.historyService;
        const url: string = historyService.generateUrl({ branch: this.state.yamlBranch });
        historyService.replaceState(undefined, url);
    }

    private _onEditorCreated = (editor: any /*IEditor*/): void => {
        if (editor) {
            this._editor = editor;
            editor.focus();
            this.setState({ isEditorReady: true });
            // if the YAML editor tab was the initial tab (and it probably was) TTI is when the editor loads
            this._markTimeToInteractive();
        }
    }

    private _onEditorDisposing = (editor: any /*IEditor*/): void => {
        let content = "";
        if (editor) {
            content = editor.getValue() || "";
        }
        this._editor = null;
        this.setState({
            yamlContent: content,
            isEditorReady: false
        })
    }

    private _resizeEditor = () => {
        if (this._editor) {
            this._editor.layout();
        }
    }

    private _onYamlContentUpdated = (newContent: string): void => {
        if (this.state.yamlContent !== newContent) {
            this.setState({
                yamlContent: newContent,
                isYamlDirty: newContent !== this._lastCleanContent
            });
        }
    }

    private _showSavePanel = () => {
        const content = (this._editor && this._editor.getValue()) || this.state.yamlContent || "";
        this.setState({
            yamlContent: content,
            savePanelIsOpen: true,
            isYamlSaving: false
        });
    }

    private _onYamlSave = (commitDescriptor: CommitDescriptor): void => {
        if (this.state.definition) {
            this.setState({ isYamlSaving: true, saveErrorMessage: "" });

            const definition = this.state.definition;
            const repository: RepositoryDescriptor = {
                connectionId: this.state.connection.connectionId,
                id: definition.repository.id,
                sourceProviderType: definition.repository.type
            };

            this.yamlFileService.commitFile(
                repository,
                this.state.yamlBranch,
                (definition.process as YamlProcess).yamlFilename,
                this.state.yamlContent,
                commitDescriptor,
                this.state.yamlFileObjectId)
                .then((savedFileInfo: SaveResult) => {
                    this._lastCleanContent = this.state.yamlContent;
                    const onSuccessfulSave = this._authorizeAfterSave ? this._authorizeAndRun : undefined;
                    this.setState({
                        isYamlDirty: false,
                        isYamlSaving: false,
                        yamlBranch: savedFileInfo.branch,
                        yamlFileObjectId: savedFileInfo.objectId,
                        yamlFileUrl: savedFileInfo.url
                    }, () => {
                        this._dismissSavePanel();
                        this._updateUrl();
                        onSuccessfulSave && onSuccessfulSave();
                    });
                }, (error: Error) => {
                    this._handleYamlSaveError(error.message);
                })
                .then(() => {
                    this._checkForHostedMac();
                });
        }
    }

    private _dismissSavePanel = () => {
        if (!this.state.isYamlSaving) {
            this._authorizeAfterSave = false;
            this.setState({
                saveErrorMessage: "",
                savePanelIsOpen: false,
            });
        }
    }


    private _showRunPanel = () => {
        this.setState({
            runPanelIsOpen: true
        });
    }

    /**
    * Queue the build.
    */
    private _runBuild = () => {
        if (!this.state.definition) {
            return;
        }
        this._onRunBuild();
        this._onBuildQueuing();

        this.pipelineService.runPipeline(this.state.definition, this.state.yamlBranch)
            .then(this._onBuildQueued, (error: Error) => {
                this._handleRunBuildError(error);
            });
    }

    private _runBuildWithParameters = () => {
        if (!this.state.definition) {
            return;
        }
        this._onRunBuild();

        const definition = this.state.definition;
        this.layoutManager.renderCallout((onDismiss) => {
            this._onQueueDialogDismiss = onDismiss;
            const props: IQueueBuildProps = {
                definition: definition,
                repository: definition.repository,
                processType: definition.process.type,
                pageContext: this.context.pageContext,
                project: definition.project.id,
                source: "yamlEditor",
                onSuccess: this._onBuildQueued,
                onClosed: this._onQueueBuildDialogClosed,
                definitionVariables: definition.variables,
                queue: definition.queue,
                defaultSourceBranch: this.state.yamlBranch,
                onDismiss,
            };

            return React.createElement(CommandComponent, {
                commandType: "ciQueueBuildComponent",
                ...props
            }, null);
        });
    }

    private _fullYaml = async () => {
        if (!this.state.definition) {
            return;
        }

        const exportedFileName = this.state.definition.name.concat(".yml");
        try {
            const result = await this._getYaml(this.state.definition);
            this._downloadFile(exportedFileName, result.finalYaml);
        }
        catch (ex) {
            this.setState({ validationDialogOpen: true, validationDialogMessage: ex.message });
            this._downloadFile(exportedFileName, ex.message);
        }
    }

    private _validateYaml = async () => {
        if (!this.state.definition) {
            return;
        }

        try {
            await this._getYaml(this.state.definition);
            this.setState({ validationDialogOpen: true, validationDialogMessage: Resources.OK });
        }
        catch (ex) {
            this.setState({ validationDialogOpen: true, validationDialogMessage: ex.message });
        }
    }

    private _getYaml = async (definition: BuildDefinition): Promise<Run> => {
        const repositoryResources = {
            refName: this.state.yamlBranch
        } as RepositoryResourceParameters;

        const resources: RunResourcesParameters = {
            pipelines: {},
            repositories: { self: repositoryResources },
            builds: {},
            containers: {},
            packages: {}
        } as RunResourcesParameters;

        const runPipelineParams: RunPipelineParameters = {
            stagesToSkip: undefined,
            resources,
            templateParameters: {},
            variables: definition.variables,
            previewRun: true,
            yamlOverride: this.state.yamlContent
        } as any as RunPipelineParameters;

        const pipelinesClient = this.context.pageContext.getRestClient<IPipelinesRestClient>("IPipelinesRestClient");
        return pipelinesClient.runPipeline(runPipelineParams, definition.project.id, definition.id);
    }

    private _downloadFile(fileName: string, content: string) {
        let file = new Blob([content], { type: "text/plain" });

        //needed for IE10
        if (window.navigator && (window.navigator as any).msSaveBlob) {
            (window.navigator as any).msSaveBlob(file, fileName);
        }
        else {
            let a = document.createElement("a");
            a.href = URL.createObjectURL(file);
            a.setAttribute("download", fileName);
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
        }
    }

    private _onRunBuild() {
        this.setState({ isBuildRunning: true });
        this._clearErrorMessage();
        this._clearBuildMessage();
    }

    private _onBuildQueuing() {
        const buildMessageInfo: BuildMessageInfo = {
            id: -1,
            number: "",
            isQueuing: true
        };
        this.setState({ buildMessageInfo }, this._resizeEditor);
    }

    private _onBuildQueued = (build: Build) => {
        if (build) {
            const buildMessageInfo: BuildMessageInfo = {
                id: build.id,
                number: build.buildNumber
            };
            this.setState({ buildMessageInfo, isBuildRunning: false }, this._resizeEditor);
        }
        else {
            this._handleRunBuildError();
        }
    }

    private _onQueueBuildDialogClosed = () => {
        // This method gets called both on success and canceling the legacy QueueBuildDialog,
        // but we need to call layoutManager.renderCallout onDismiss exactly once to dispose resources.
        if (this._onQueueDialogDismiss) {
            const onDismiss = this._onQueueDialogDismiss;
            this._onQueueDialogDismiss = undefined;
            onDismiss();
        }
        if (this.state.isBuildRunning) {
            this.setState({ isBuildRunning: false });
        }
    }

    private _onRunPanelClosed = () => {
        this.setState({runPanelIsOpen: false});
    }

    /**
     * Attempt to automatically authorize resources referenced in the YAML file.
     */
    private _autoAuthorizeResources = (): Promise<void> => {
        const pipelineResourcesService = this.context.pageContext.getService<IPipelineResourcesService>("IPipelineResourcesService");
        return pipelineResourcesService.autoAuthorizeResources(this.state.definition!.id, this.state.yamlBranch)
            .then((resources: DefinitionResourceReference[]) => {
                // TODO: Consider UI to indicate new resource authorization
            }, (error: Error) => {
                console.warn("YAML editor: Error while auto-authorizing resources: " + error && error.message);
            });
    }

    private _authorizeAndRun = () => {
        this._clearErrorMessage();

        if (this.state.isYamlDirty) {
            this._authorizeAfterSave = true;
            this._showSavePanel();
        }
        else {
            this._autoAuthorizeResources().then(() => this._runBuild());
        }
    }

    private _showPipelineSettingsPanel = () => {
        this.setState({
            settingsPanelIsOpen: true,
        });
    }

    private _onSavePipelineSettings = (changes: Partial<PipelineSettings>) => {
        if (!changes || !Object.keys(changes).length) {
            return;
        }

        let requiresReload = false;
        const comments: string[] = [];
        const definition = { ...this.state.definition! };
        this.setState({ isPipelineSaving: true, settingsErrorMessage: "" });

        // Update Queue Status
        if (changes.status !== undefined) {
            definition.queueStatus = changes.status;
            switch (changes.status) {
                case DefinitionQueueStatus.Disabled:
                    comments.push(LibraryResources.SavePipelineCommentDisable);
                    break;
                case DefinitionQueueStatus.Enabled:
                    comments.push(LibraryResources.SavePipelineCommentEnable);
                    break;
                case DefinitionQueueStatus.Paused:
                    comments.push(LibraryResources.SavePipelineCommentPause);
                    break;
                default:
                    comments.push(LibraryResources.SavePipelineCommentUnknownStatus);
                    break;
            }
        }

        // Update YAML file path
        if (changes.yamlFilePath !== undefined) {
            requiresReload = true;
            const yamlProcess = { ...definition.process } as YamlProcess;
            yamlProcess.yamlFilename = changes.yamlFilePath;
            definition.process = yamlProcess;
            comments.push(format(LibraryResources.SavePipelineCommentYamlFile, changes.yamlFilePath));
        }

        // Update Auto Work Item Linking Branch
        if (changes.witLinkingEnabled !== undefined || changes.witLinkingBranchFilter) {
            if (!definition.options) {
                definition.options = [];
            }
            let option = definition.options.filter(o => equals(o.definition.id, BuildOptionGUIDs.WitLinkingOptionGUID))[0];
            if (changes.witLinkingBranchFilter) {
                comments.push(format(LibraryResources.SavePipelineCommentUpdateAutoLinkBranch, changes.witLinkingBranchFilter));
                if (!option) {
                    option = {
                        definition: {
                            id: BuildOptionGUIDs.WitLinkingOptionGUID
                        },
                        inputs: {
                            "branchFilters": JSON.stringify([changes.witLinkingBranchFilter])
                        },
                        enabled: true
                    }
                    definition.options.push(option);
                } else {
                    option.inputs = {
                        "branchFilters": JSON.stringify([changes.witLinkingBranchFilter])
                    }
                }
            }
            if (changes.witLinkingEnabled !== undefined && option) {
                comments.push(format(LibraryResources.SavePipelineCommentEnableAutoLink, changes.witLinkingEnabled));
                option.enabled = changes.witLinkingEnabled;
            }
        }

        // Concatenate change comments for pipeline history.  Consider a dialog for a user to edit when/if needed.
        definition.comment = comments.join(". ");

        this.pipelineService.savePipeline(definition)
            .then((definition: BuildDefinition) => {
                this._showPipelineSavedToast();
                if (requiresReload) {
                    this.setState({ isYamlDirty: false, isPipelineSaving: false },
                        () => FastPageSwitch(this.context.pageContext, window.location.href, false));
                }
                else {
                    this.setState({ definition, isPipelineSaving: false }, this._dismissPipelineSettingsPanel);
                }
            }, (error: Error) => {
                const message = (error && error.message) || LibraryResources.SavePipelineErrorMessage;
                this._handleSettingsError(message);
            });
    }

    private _dismissPipelineSettingsPanel = () => {
        if (!this.state.isPipelineSaving) {
            this.setState({
                settingsPanelIsOpen: false,
                settingsErrorMessage: "",
                isPipelineSaving: false
            });
        }
    }

    private _handleSettingsError = (errorMessage: string) => {
        this.setState({
            settingsErrorMessage: errorMessage,
            isPipelineSaving: false,
            settingsPanelIsOpen: true
        });
    }

    private _showPipelineVariablesPanel = () => {
        this.setState({
            variablesPanelIsOpen: true,
        });
    }

    private _dismissPipelineVariablesPanel = () => {
        if (!this.state.isPipelineSaving) {
            this.setState({
                variablesPanelIsOpen: false,
                variablesErrorMessage: "",
                isPipelineSaving: false
            });
        }
    }

    private _onSavePipelineVariables = (variables: {[name: string]: BuildDefinitionVariable}) => {
        const comments: string[] = [];
        const definition = { ...this.state.definition! };
        this.setState({ isPipelineSaving: true, variablesErrorMessage: "" });

        definition.variables = variables;

        // Generic "Update variables" comment.  Consider listing new, deleted, and modified variables.
        comments.push(Resources.VariablesPanelSaveComment);

        // Concatenate change comments for pipeline history.  Consider a dialog for a user to edit when/if needed.
        definition.comment = comments.join(". ");

        this.pipelineService.savePipeline(definition)
            .then((definition: BuildDefinition) => {
                this._showPipelineSavedToast();
                this.setState({ definition, isPipelineSaving: false }, this._dismissPipelineVariablesPanel);
            }, (error: Error) => {
                const message = (error && error.message) || LibraryResources.SavePipelineErrorMessage;
                this._handleVariablesError(message);
            });
    }

    private _showPipelineSavedToast() {
        this.globalMessageService.addToast({
            message: Resources.PipelineSavedMessage,
            duration: EditorConstants.ToastDuration
        });
    }

    private _handleVariablesError = (errorMessage: string) => {
        this.setState({
            variablesErrorMessage: errorMessage,
            isPipelineSaving: false,
            variablesPanelIsOpen: true
        });
    }

    private _handleEditorDataProviderError(exception: IServerException, state: State) {
        let errorMessage = format(Resources.EditorErrorDefaultPipelineFetch, (exception && exception.message) || "");
        let errorActions: IButtonProps[] | undefined;

        // Help the user navigate to the classic pipeline editor for fixable issues (ex: service connection)
        if (exception && exception.exceptionType) {
            const pipelineId = Number(this.historyService.getState().pipelineId);
            if (pipelineId > 0) {
                let showClassicEditorButton = true;
                switch (exception.exceptionType) {
                    case WellKnownExceptions.ServiceEndpointNotFoundException:
                        errorMessage = format(Resources.EditorErrorServiceConnection, exception.message);
                        break;
                    case WellKnownExceptions.GitRepositoryNotFoundException:
                        errorMessage = format(Resources.EditorErrorGitRepoNotFound, exception.message);
                        break;
                    case WellKnownExceptions.DefinitionTypeNotSupportedException:
                        errorMessage = format(Resources.EditorErrorNotYamlPipeline, exception.message)
                        break;
                    default:
                        showClassicEditorButton = false;
                        break;
                }
                if (showClassicEditorButton) {
                    const definitionEditUrl = this.buildLinkingService.getDefinitionEditUrl(pipelineId, LegacyDesignerTabKeys.Yaml);
                    errorActions = [{
                        text: Resources.EditInOldDesigner,
                        href: definitionEditUrl
                    }];
                }
            }
        }
        state.errorMessage = errorMessage;
        state.errorActions = errorActions;
    }

    private _handleRunBuildError = (error?: Error) => {
        this.setState({ isBuildRunning: false });

        let errorMessage = (error && error.message) || Resources.RunBuildErrorMessage;
        let errorActions: IButtonProps[] | undefined;

        // Attempt to show more error detail if it was returned as BuildRequestValidationResults.
        const vssError = error as VssServerError;
        if (vssError && vssError.serverError && vssError.serverError.customProperties) {
            const validationResults: BuildRequestValidationResult[] = vssError.serverError.customProperties.ValidationResults;
            if (validationResults && validationResults.length > 0) {
                const messages: string[] = validationResults.map((result) => { return result.message });
                errorMessage += "\n" + messages.join("\n");

                // Show the Authorize Resources action if that appears to be the reason for the failure
                const yamlAuthorizationErrorUrl = "https://aka.ms/yamlauthz"
                if (errorMessage.indexOf(yamlAuthorizationErrorUrl) > 0) {
                    errorActions = [
                        { primary: true, text: Resources.AuthorizeAndRunButtonText, onClick: this._authorizeAndRun }
                    ];
                }
            }
        }
        this._handleError(errorMessage, true, errorActions);
    }

    private _handleError = (errorMessage: string, isDissmissable: boolean, errorActions?: IButtonProps[] | undefined) => {
        this._clearBuildMessage();
        this.setState({
            errorMessage: errorMessage,
            isErrorMessageDismissable: isDissmissable,
            errorActions
        }, this._resizeEditor);
    }

    private _handleYamlSaveError = (errorMessage: string) => {
        this.setState({
            saveErrorMessage: errorMessage,
            isYamlSaving: false,
            savePanelIsOpen: true
        });
    }

    private _markTimeToInteractive = (): void => {
        if (!this._ttiRecorded) {
            this._ttiRecorded = true;
            this.context.pageContext.getService<IVssPerformanceService>("IVssPerformanceService").markTimeToInteractive(PerformanceConstants.Area, PageLoadScenarios.YamlEditor);
        }
    }

    private _onChangeVariables = (resourceVariables: NamedBuildDefinitionVariable[], action: string): void => {
        this.setState({
            isVariablesDirty: this._yamlResourceService.isVariablesChanged()
        });
    }

    private _onBranchSelected = (branchName: string) => {
        if (branchName !== this.state.yamlBranch) {
            if (this.state.isYamlDirty) {
                this.layoutManager.renderCallout((onDismiss) => {
                    const leave = () => {
                        onDismiss();
                        this._switchBranch(branchName);
                    };
                    const stay = () => { onDismiss() };
                    return getLoseChangesDialog(leave, stay);
                });
            }
            else {
                this._switchBranch(branchName);
            }
        }
    }

    private _switchBranch = (branchName: string) => {
        this.setState({
            yamlContent: "",
            yamlFileObjectId: "",
            yamlBranch: branchName,
            yamlFileUrl: ""
        });
        this._clearErrorMessage();

        const historyService = this.historyService;
        const url: string = historyService.generateUrl({ branch: branchName });
        historyService.pushState(undefined, url);

        const definition = this.state.definition!;
        const fileName = (definition.process as YamlProcess).yamlFilename;
        const buildRepo = definition.repository;
        const repository: RepositoryDescriptor = {
            connectionId: buildRepo.properties && buildRepo.properties["connectedServiceId"],
            id: buildRepo.id,
            name: buildRepo.name,
            sourceProviderType: buildRepo.type
        };

        this.yamlFileService.getFileContentData(
            repository,
            branchName,
            fileName)
            .then((fileInfo: FileContentInfo) => {
                this._updateFileInfo(fileInfo);
            }, (error: Error) => {
                this._updateFileInfo({
                    content: "",
                    branch: branchName,
                    path: fileName,
                    objectId: "",
                    url: ""
                });
                this._handleError(error.message, false);
            })
            .then(() => {
                this._checkForHostedMac();
            });
    }

    private _updateFileInfo(fileInfo: FileContentInfo) {
        this._lastCleanContent = fileInfo.content;
        this.setState({
            isYamlDirty: false,
            isYamlSaving: false,
            yamlBranch: fileInfo.branch,
            yamlFileObjectId: fileInfo.objectId,
            yamlContent: fileInfo.content,
            yamlFileUrl: fileInfo.url
        });
    }

    private _checkForHostedMac() {
        if (this.state.definition) {
            const CheckForHostedMacDataProviderKey = "ms.vss-build-web.check-for-hosted-mac-data-provider";
            const requestParams: CheckForHostedMacDataRequestParams = {
                pipelineId: this.state.definition.id,
                sourceBranch: this.state.yamlBranch
            };
    
            const contributionService = this.context.pageContext.getService<IVssContributionService>("IVssContributionService");
            contributionService.getDataAsync<CheckForHostedMacData>(CheckForHostedMacDataProviderKey, requestParams, true).then(usingHostedMacData => {
                if (usingHostedMacData) {
                    if (usingHostedMacData.usingHostedMac) {
                        const currentBanner = this.globalMessageService.getBanner();
                        if (!currentBanner || currentBanner.settingId !== hostedMacWarningBannerId) {
                            const bannerContent: BannerContent = {
                                id: hostedMacWarningBannerId,
                                links: [{
                                    href: hostedMacLearnMoreLink,
                                    name: Resources.LearnMore,
                                    supportsFPS: false
                                }],
                                messageFormat: Resources.HostedMacWarning
                            };

                            showWarning(this.context.pageContext, bannerContent);
                        }
                    }
                    else {
                        clearWarning(this.context.pageContext, hostedMacWarningBannerId)
                    }
                }
            });
        }
    }

    private get breadcrumbService(): IPipelineEditorBreadcrumbService {
        return this.context.pageContext.getService<IPipelineEditorBreadcrumbService>("pipeline-editor-breadcrumb-service");
    }

    private get buildLinkingService(): IBuildLinkingService {
        return this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService")
    }

    private get historyService(): IVssHistoryService {
        return this.context.pageContext.getService<IVssHistoryService>("IVssHistoryService");
    }

    private get layoutManager(): IVssLayoutManager {
        return this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
    }

    private get navigationService(): ITfsNavigationService {
        return this.context.pageContext.getService<ITfsNavigationService>("ITfsNavigationService");
    }

    private get pipelineService(): IPipelineService {
        return this.context.pageContext.getService<IPipelineService>("IPipelineService");
    }

    private get globalMessageService(): IGlobalMessagesService {
        return this.context.pageContext.getService<IGlobalMessagesService>("IGlobalMessagesService");
    }

    private get yamlFileService(): IYamlFileService {
        return this.context.pageContext.getService<IYamlFileService>("IYamlFileService");
    }

}

const pageClassName = "pipeline-editor-view bolt-page-grey flex-column flex-grow";
const hostedMacWarningBannerId = "hosted-mac-warning";
const hostedMacLearnMoreLink = "https://go.microsoft.com/fwlink/?linkid=870426";

Components.add("ci-designer-editor-view", EditorView);
